import React, { useState, useEffect, useCallback } from 'react';
import { Download, Upload, Loader, Trash2, RefreshCw, HardDrive, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { FormField } from '@/components/shared/FormDialog';
import { useSettings } from '@/hooks/useSettings';
import { AdminUsersManagement } from '@/components/admin/AdminUsersManagement';
import { EmailSettingsSection } from '@/components/admin/EmailSettingsSection';
import CancellationPolicySettings from '@/components/admin/CancellationPolicySettings';

/**
 * Settings Panel Component
 * Manage payment settings, Beds24 integration, and backup/restore
 */
export const SettingsPanel = ({ API }) => {
  const {
    settings,
    loading,
    updateSettings,
    updatePaymentSettings,
    updateBeds24Credentials,
    restartBackend,
    exportBackup,
    importBackup
  } = useSettings(API);

  const [settingsTab, setSettingsTab] = useState('payment');
  const [backupFile, setBackupFile] = useState(null);
  const [nexiSettings, setNexiSettings] = useState({
    api_key: '',
    environment: 'test',
    demo_mode: false
  });
  const [nexiLoading, setNexiLoading] = useState(false);

  const [stripeSettings, setStripeSettings] = useState({
    api_key: '',
    webhook_secret: '',
    mode: 'test'
  });
  const [stripeLoading, setStripeLoading] = useState(false);

  const [paypalSettings, setPaypalSettings] = useState({
    client_id: '',
    client_secret: '',
    environment: 'sandbox',
    webhook_id: ''
  });
  const [paypalLoading, setPaypalLoading] = useState(false);

  // Fetch Nexi settings
  React.useEffect(() => {
    if (settingsTab === 'nexi') {
      fetchNexiSettings();
    }
  }, [settingsTab]);

  // Fetch Stripe settings
  React.useEffect(() => {
    if (settingsTab === 'stripe') {
      fetchStripeSettings();
    }
  }, [settingsTab]);

  // Fetch PayPal settings
  React.useEffect(() => {
    if (settingsTab === 'paypal') {
      fetchPaypalSettings();
    }
  }, [settingsTab]);

  const fetchNexiSettings = async () => {
    try {
      setNexiLoading(true);
      const response = await fetch(`${API}/admin/nexi-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNexiSettings(data);
      }
    } catch (error) {
      console.error('Error fetching Nexi settings:', error);
    } finally {
      setNexiLoading(false);
    }
  };

  const fetchStripeSettings = async () => {
    try {
      setStripeLoading(true);
      const response = await fetch(`${API}/admin/stripe-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStripeSettings({
          api_key: data.api_key_masked || '',
          webhook_secret: data.webhook_secret || '',
          mode: data.mode || 'test'
        });
      }
    } catch (error) {
      console.error('Error fetching Stripe settings:', error);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSaveNexiSettings = async (e) => {
    e.preventDefault();
    if (!nexiSettings.api_key) {
      alert('API Key is required');
      return;
    }
    try {
      setNexiLoading(true);
      const response = await fetch(`${API}/admin/nexi-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(nexiSettings)
      });

      if (response.ok) {
        alert('Nexi settings updated successfully. Changes will take effect immediately.');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update Nexi settings');
      }
    } catch (error) {
      console.error('Error saving Nexi settings:', error);
      alert('Error saving Nexi settings');
    } finally {
      setNexiLoading(false);
    }
  };

  const handleSaveStripeSettings = async (e) => {
    e.preventDefault();
    if (!stripeSettings.api_key || stripeSettings.api_key.startsWith('sk_****')) {
      alert('Please enter a valid Stripe API key');
      return;
    }
    try {
      setStripeLoading(true);
      const response = await fetch(`${API}/admin/stripe-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(stripeSettings)
      });

      if (response.ok) {
        alert('Stripe settings updated successfully. Changes will take effect immediately.');
        // Refresh to show masked key
        fetchStripeSettings();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update Stripe settings');
      }
    } catch (error) {
      console.error('Error saving Stripe settings:', error);
      alert('Error saving Stripe settings');
    } finally {
      setStripeLoading(false);
    }
  };

  // PayPal settings functions
  const fetchPaypalSettings = async () => {
    try {
      setPaypalLoading(true);
      const response = await fetch(`${API}/admin/paypal-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPaypalSettings({
          client_id: data.client_id || '',
          client_secret: data.client_secret_masked || '',
          environment: data.environment || 'sandbox',
          webhook_id: data.webhook_id || ''
        });
      }
    } catch (error) {
      console.error('Error fetching PayPal settings:', error);
    } finally {
      setPaypalLoading(false);
    }
  };

  const handleSavePaypalSettings = async (e) => {
    e.preventDefault();
    if (!paypalSettings.client_id) {
      alert('Please enter a PayPal Client ID');
      return;
    }
    try {
      setPaypalLoading(true);
      const response = await fetch(`${API}/admin/paypal-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(paypalSettings)
      });

      if (response.ok) {
        alert('PayPal settings updated successfully.');
        fetchPaypalSettings();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update PayPal settings');
      }
    } catch (error) {
      console.error('Error saving PayPal settings:', error);
      alert('Error saving PayPal settings');
    } finally {
      setPaypalLoading(false);
    }
  };

  // Local state for payment settings
  const [paymentSettings, setPaymentSettings] = useState({
    enable_stripe: settings.enable_stripe ?? true,
    enable_nexi: settings.enable_nexi ?? false,
    enable_paypal: settings.enable_paypal ?? false,
    enable_pay_on_arrival: settings.enable_pay_on_arrival ?? true,
    enable_non_refundable: settings.enable_non_refundable ?? true,
    payment_reminders_enabled: settings.payment_reminders_enabled ?? true,
    flexible_rate_percentage: settings.flexible_rate_percentage || 30,
    environmental_fee: settings.environmental_fee ?? 5,
    non_refundable_discount: settings.non_refundable_discount ?? 10
  });

  // Sync payment settings when settings change
  React.useEffect(() => {
    setPaymentSettings({
      enable_stripe: settings.enable_stripe ?? true,
      enable_nexi: settings.enable_nexi ?? false,
      enable_paypal: settings.enable_paypal ?? false,
      enable_pay_on_arrival: settings.enable_pay_on_arrival ?? true,
      enable_non_refundable: settings.enable_non_refundable ?? true,
      payment_reminders_enabled: settings.payment_reminders_enabled ?? true,
      flexible_rate_percentage: settings.flexible_rate_percentage || 30,
      environmental_fee: settings.environmental_fee ?? 5,
      non_refundable_discount: settings.non_refundable_discount ?? 10
    });
  }, [settings]);

  // Local state for Beds24 credentials
  const [beds24Credentials, setBeds24Credentials] = useState({
    beds24_api_key: settings.beds24_api_key || '',
    beds24_prop_key: settings.beds24_prop_key || '',
    beds24_property_id: settings.beds24_property_id || ''
  });

  // Beds24 sync settings state
  const [beds24WebhookUrl, setBeds24WebhookUrl] = useState('');
  const [autoPollEnabled, setAutoPollEnabled] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [pollingBeds24, setPollingBeds24] = useState(false);
  const [syncingInventory, setSyncingInventory] = useState(false);

  // Fetch Beds24 sync settings
  React.useEffect(() => {
    if (settingsTab === 'beds24') {
      fetchBeds24SyncSettings();
    }
  }, [settingsTab]);

  const fetchBeds24SyncSettings = async () => {
    try {
      // Get webhook URL
      const webhookRes = await fetch(`${API}/admin/beds24/webhook-url`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (webhookRes.ok) {
        const data = await webhookRes.json();
        setBeds24WebhookUrl(data.webhook_url);
      }

      // Get sync settings
      const syncRes = await fetch(`${API}/admin/beds24/sync-settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (syncRes.ok) {
        const data = await syncRes.json();
        setAutoPollEnabled(data.auto_poll_enabled);
      }
    } catch (error) {
      console.error('Error fetching Beds24 sync settings:', error);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const response = await fetch(`${API}/admin/beds24/test-webhook`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('✅ Webhook connection successful!');
      } else {
        toast.error(`❌ Webhook test failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Error testing webhook connection');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handlePollBeds24 = async () => {
    setPollingBeds24(true);
    try {
      const response = await fetch(`${API}/admin/beds24/poll-bookings`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days_back: 7, days_forward: 60 })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ Poll complete! Imported: ${data.imported}, Updated: ${data.updated}, Skipped: ${data.skipped}`);
      } else {
        toast.error(`❌ Poll failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Error polling Beds24');
    } finally {
      setPollingBeds24(false);
    }
  };

  const handleToggleAutoPoll = async (enabled) => {
    try {
      const response = await fetch(`${API}/admin/beds24/sync-settings`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ auto_poll_enabled: enabled })
      });
      
      if (response.ok) {
        setAutoPollEnabled(enabled);
        toast.success(enabled ? 'Auto-poll enabled' : 'Auto-poll disabled');
      }
    } catch (error) {
      toast.error('Error updating auto-poll setting');
    }
  };

  const handleSyncInventoryFromBeds24 = async () => {
    setSyncingInventory(true);
    try {
      const response = await fetch(`${API}/admin/beds24/sync-inventory`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Inventory synced: ${data.total_entries || 0} entries, ${data.room_types_synced || 0} room types`);
      } else {
        toast.error(data.detail || 'Failed to sync inventory');
      }
    } catch (error) {
      toast.error('Error syncing inventory from Beds24');
    } finally {
      setSyncingInventory(false);
    }
  };

  // Sync Beds24 credentials when settings change
  React.useEffect(() => {
    setBeds24Credentials({
      beds24_api_key: settings.beds24_api_key || '',
      beds24_prop_key: settings.beds24_prop_key || '',
      beds24_property_id: settings.beds24_property_id || ''
    });
  }, [settings]);

  const handleSavePaymentSettings = async () => {
    await updatePaymentSettings(paymentSettings);
  };

  const handleSaveBeds24 = async () => {
    await updateBeds24Credentials(beds24Credentials);
  };

  const handleRestartBackend = async () => {
    await restartBackend();
  };

  const handleExportBackup = async () => {
    await exportBackup();
  };

  const handleImportBackup = async () => {
    if (!window.confirm('⚠️ WARNING: This will REPLACE all existing data. Are you sure?')) {
      return;
    }
    await importBackup(backupFile);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Settings</h2>
        <p className="text-sm text-gray-600">Configure application settings and integrations</p>
      </div>

      {/* Settings Sub-tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setSettingsTab('payment')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'payment'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Payment Settings
          </button>
          <button
            onClick={() => setSettingsTab('beds24')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'beds24'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Beds24 Integration
          </button>
          <button
            onClick={() => setSettingsTab('backup')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'backup'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Backup & Restore
          </button>
          <button
            onClick={() => setSettingsTab('admin-users')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'admin-users'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Admin Users
          </button>
          <button
            onClick={() => setSettingsTab('nexi')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'nexi'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Nexi XPay
          </button>
          <button
            onClick={() => setSettingsTab('stripe')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'stripe'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Stripe
          </button>
          <button
            onClick={() => setSettingsTab('paypal')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'paypal'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            PayPal
          </button>
          <button
            onClick={() => setSettingsTab('email')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'email'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            📧 Email
          </button>
          <button
            onClick={() => setSettingsTab('cancellation')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'cancellation'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            📋 Cancellation
          </button>
          <button
            onClick={() => setSettingsTab('branding')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'branding'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            🎨 Branding
          </button>
          <button
            onClick={() => setSettingsTab('database')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'database'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            🗄️ Database
          </button>
          <button
            onClick={() => setSettingsTab('debug')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              settingsTab === 'debug'
                ? 'border-gray-600 text-gray-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            🐛 Debug Logs
          </button>
          <button
            onClick={() => setSettingsTab('min-stay')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              settingsTab === 'min-stay'
                ? 'border-teal-600 text-teal-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Min Stay
          </button>
          <button
            onClick={() => setSettingsTab('beds24-audit')}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              settingsTab === 'beds24-audit'
                ? 'border-rose-600 text-rose-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            Beds24 Audit
          </button>
        </nav>
      </div>

      {/* Payment Settings Tab */}
      {settingsTab === 'payment' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <div className="space-y-6">
            {/* Flexible Rate Deposit */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Flexible Rate Deposit Percentage</h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="deposit_percentage">Deposit Amount (%)</Label>
                  <Input
                    id="deposit_percentage"
                    type="number"
                    min="1"
                    max="100"
                    value={paymentSettings.flexible_rate_percentage}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      flexible_rate_percentage: parseFloat(e.target.value) 
                    })}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-blue-700 mb-1">
                    Current: {paymentSettings.flexible_rate_percentage}%
                  </p>
                  <p className="text-xs text-blue-600">
                    Example: €150 → €{(150 * paymentSettings.flexible_rate_percentage / 100).toFixed(2)} deposit
                  </p>
                </div>
              </div>
            </div>

            {/* Environmental Fee */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">🌿 Environmental Fee</h3>
              <p className="text-sm text-emerald-700 mb-3">
                This fee is charged per night per room and is paid at check-in.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="environmental_fee">Amount per Night (€)</Label>
                  <Input
                    id="environmental_fee"
                    type="number"
                    min="0"
                    step="0.5"
                    value={paymentSettings.environmental_fee}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      environmental_fee: parseFloat(e.target.value) || 0 
                    })}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-emerald-700 mb-1">
                    Current: €{paymentSettings.environmental_fee} per night
                  </p>
                  <p className="text-xs text-emerald-600">
                    Example: 3 nights × 2 rooms = €{(paymentSettings.environmental_fee * 3 * 2).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Non-Refundable Discount */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Non-Refundable Rate Discount</h3>
              <p className="text-sm text-amber-700 mb-3">
                Discount applied when guests choose the non-refundable payment option. Full amount is charged upfront.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="non_refundable_discount">Discount Percentage (%)</Label>
                  <Input
                    id="non_refundable_discount"
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={paymentSettings.non_refundable_discount}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      non_refundable_discount: parseFloat(e.target.value) || 0 
                    })}
                    className="mt-1"
                    data-testid="non-refundable-discount-input"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-amber-700 mb-1">
                    Current: {paymentSettings.non_refundable_discount}% discount
                  </p>
                  <p className="text-xs text-amber-600">
                    Example: €150 room → €{(150 * (1 - paymentSettings.non_refundable_discount / 100)).toFixed(2)} non-refundable price
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Options */}
            <div className="space-y-4">
              {/* Stripe */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Flexible Rate (Stripe)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {paymentSettings.flexible_rate_percentage}% deposit via Stripe
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.enable_stripe}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      enable_stripe: e.target.checked 
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Nexi */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-orange-200">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Flexible Rate (Nexi XPay)</h3>
                  <p className="text-sm text-gray-600 mt-1">Alternative payment via Nexi</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.enable_nexi}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      enable_nexi: e.target.checked 
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>

              {/* PayPal */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-blue-200">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">🅿️ PayPal</h3>
                  <p className="text-sm text-gray-600 mt-1">Accept PayPal payments (configure in PayPal tab)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.enable_paypal}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      enable_paypal: e.target.checked 
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>

              {/* Non-Refundable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Non-Refundable Rate</h3>
                  <p className="text-sm text-gray-600 mt-1">Discounted, non-refundable</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.enable_non_refundable}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      enable_non_refundable: e.target.checked 
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Pay on Arrival */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Pay on Arrival</h3>
                  <p className="text-sm text-gray-600 mt-1">No prepayment required</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.enable_pay_on_arrival}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      enable_pay_on_arrival: e.target.checked 
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-600"></div>
                </label>
              </div>
            </div>

            {/* Payment Reminders Toggle */}
            <div className="pt-4 border-t">
              <h4 className="text-md font-medium mb-3">Email Notifications</h4>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">Automatic Payment Reminders</p>
                  <p className="text-sm text-gray-600 mt-1">Send email reminders to guests with pending payments</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={paymentSettings.payment_reminders_enabled}
                    onChange={(e) => setPaymentSettings({ 
                      ...paymentSettings, 
                      payment_reminders_enabled: e.target.checked 
                    })}
                    className="sr-only peer"
                    data-testid="payment-reminders-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            <Button
              onClick={handleSavePaymentSettings}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              {loading ? 'Saving...' : 'Save Payment Settings'}
            </Button>
          </div>
        </div>
      )}

      {/* Beds24 Integration Tab */}
      {settingsTab === 'beds24' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <div className="space-y-6">
            <div className="pb-4 border-b">
              <h3 className="text-lg font-semibold">Beds24 Channel Manager</h3>
              <p className="text-sm text-gray-600 mt-1">Configure API credentials</p>
            </div>

            <FormField label="API Key" htmlFor="beds24_api_key">
              <Input
                id="beds24_api_key"
                type="text"
                value={beds24Credentials.beds24_api_key}
                onChange={(e) => setBeds24Credentials({ 
                  ...beds24Credentials, 
                  beds24_api_key: e.target.value 
                })}
                placeholder="a9149018-955a-460f-96f5-b53dcdd04ad5"
                className="font-mono text-sm"
              />
            </FormField>

            <FormField label="Property Key" htmlFor="beds24_prop_key">
              <Input
                id="beds24_prop_key"
                type="text"
                value={beds24Credentials.beds24_prop_key}
                onChange={(e) => setBeds24Credentials({ 
                  ...beds24Credentials, 
                  beds24_prop_key: e.target.value 
                })}
                placeholder="95370c3e-9000-4147-90e8-f1741cff3a8e"
                className="font-mono text-sm"
              />
            </FormField>

            <FormField label="Property ID" htmlFor="beds24_property_id">
              <Input
                id="beds24_property_id"
                type="text"
                value={beds24Credentials.beds24_property_id}
                onChange={(e) => setBeds24Credentials({ 
                  ...beds24Credentials, 
                  beds24_property_id: e.target.value 
                })}
                placeholder="30278"
              />
            </FormField>

            <div className="space-y-3">
              <Button
                onClick={handleSaveBeds24}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
              >
                {loading ? 'Saving...' : 'Save Beds24 Credentials'}
              </Button>
              
              <Button
                onClick={handleRestartBackend}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-600 to-red-600"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Restarting...
                  </>
                ) : (
                  '🔄 Restart Backend Service'
                )}
              </Button>
            </div>

            {/* Beds24 Sync Settings */}
            <div className="pt-6 border-t mt-6">
              <h4 className="text-md font-semibold mb-4">Booking Sync Settings</h4>
              
              {/* Webhook URL */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <Label className="text-sm font-medium text-blue-800">Webhook URL (Configure in Beds24)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={beds24WebhookUrl}
                    readOnly
                    className="font-mono text-xs bg-white"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(beds24WebhookUrl);
                      toast.success('Webhook URL copied!');
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Paste this URL in Beds24: Settings → Properties → Access → Booking Webhooks
                </p>
              </div>

              {/* Test Webhook Button */}
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={handleTestWebhook}
                  disabled={testingWebhook}
                >
                  {testingWebhook ? 'Testing...' : '🔗 Test Webhook Connection'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="border-purple-600 text-purple-600 hover:bg-purple-50"
                  onClick={handlePollBeds24}
                  disabled={pollingBeds24}
                  data-testid="poll-beds24-btn"
                >
                  {pollingBeds24 ? 'Polling...' : 'Poll Beds24 Now'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                  onClick={handleSyncInventoryFromBeds24}
                  disabled={syncingInventory}
                  data-testid="sync-inventory-from-beds24-btn"
                >
                  {syncingInventory ? 'Syncing...' : 'Sync Inventory from Beds24'}
                </Button>
              </div>

              {/* Auto Poll Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Auto-Poll Beds24</Label>
                  <p className="text-xs text-gray-500">Automatically check for new bookings every 5 minutes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoPollEnabled}
                    onChange={(e) => handleToggleAutoPoll(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nexi XPay Settings Tab */}
      {settingsTab === 'nexi' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <div className="space-y-6">
            <div className="pb-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-2xl">🟧</span>
                Nexi XPay Payment Gateway
              </h3>
              <p className="text-sm text-gray-600 mt-1">Configure Nexi XPay credentials for payment processing</p>
            </div>

            <form onSubmit={handleSaveNexiSettings} className="space-y-6">
              <FormField label="API Key" htmlFor="nexi_api_key" required>
                <Input
                  id="nexi_api_key"
                  type="text"
                  value={nexiSettings.api_key}
                  onChange={(e) => setNexiSettings({ ...nexiSettings, api_key: e.target.value })}
                  placeholder="Enter your Nexi API key"
                  className="font-mono text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Get your API key from Nexi developer portal
                </p>
              </FormField>

              <FormField label="Environment" htmlFor="nexi_environment">
                <select
                  id="nexi_environment"
                  value={nexiSettings.environment}
                  onChange={(e) => setNexiSettings({ ...nexiSettings, environment: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="test">Test (Sandbox)</option>
                  <option value="production">Production (Live)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Use Test for development, Production for live payments
                </p>
              </FormField>

              <FormField label="Demo Mode" htmlFor="nexi_demo_mode">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="nexi_demo_mode"
                    type="checkbox"
                    checked={nexiSettings.demo_mode}
                    onChange={(e) => setNexiSettings({ ...nexiSettings, demo_mode: e.target.checked })}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Enable demo mode (no real API calls)</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Demo mode simulates payment flows without contacting Nexi API
                </p>
              </FormField>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={nexiLoading}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600"
                >
                  {nexiLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Nexi Settings'
                  )}
                </Button>
              </div>
            </form>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Configuration Notes</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Settings are saved to database and take effect immediately</li>
                <li>• No server restart required</li>
                <li>• Test your integration in sandbox mode before going live</li>
                <li>• Contact Nexi support for production credentials</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Settings Tab */}
      {settingsTab === 'stripe' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <div className="space-y-6">
            <div className="pb-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-2xl">💳</span>
                Stripe Payment Gateway
              </h3>
              <p className="text-sm text-gray-600 mt-1">Configure Stripe credentials for payment processing</p>
            </div>

            <form onSubmit={handleSaveStripeSettings} className="space-y-6">
              <FormField label="API Key" htmlFor="stripe_api_key" required>
                <Input
                  id="stripe_api_key"
                  type="text"
                  value={stripeSettings.api_key}
                  onChange={(e) => setStripeSettings({ ...stripeSettings, api_key: e.target.value })}
                  placeholder="sk_test_... or sk_live_..."
                  className="font-mono text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Starts with <code className="bg-gray-100 px-1 rounded">sk_test_</code> for test mode or <code className="bg-gray-100 px-1 rounded">sk_live_</code> for live mode
                </p>
              </FormField>

              <FormField label="Webhook Secret (Optional)" htmlFor="stripe_webhook_secret">
                <Input
                  id="stripe_webhook_secret"
                  type="text"
                  value={stripeSettings.webhook_secret}
                  onChange={(e) => setStripeSettings({ ...stripeSettings, webhook_secret: e.target.value })}
                  placeholder="whsec_..."
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required for webhook validation (get from Stripe Dashboard)
                </p>
              </FormField>

              <FormField label="Mode" htmlFor="stripe_mode">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stripe_mode"
                      value="test"
                      checked={stripeSettings.mode === 'test'}
                      onChange={(e) => setStripeSettings({ ...stripeSettings, mode: e.target.value })}
                      className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <span className="text-sm">Test Mode</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stripe_mode"
                      value="live"
                      checked={stripeSettings.mode === 'live'}
                      onChange={(e) => setStripeSettings({ ...stripeSettings, mode: e.target.value })}
                      className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <span className="text-sm">Live Mode</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Mode is auto-detected from API key prefix, but can be manually set
                </p>
              </FormField>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={stripeLoading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
                >
                  {stripeLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Stripe Settings'
                  )}
                </Button>
              </div>
            </form>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Configuration Notes</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Settings are saved to database and take effect immediately</li>
                <li>• No server restart required</li>
                <li>• API key is masked for security after saving</li>
                <li>• Get your keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a></li>
                <li>• Always test with test keys before using live keys</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* PayPal Settings Tab */}
      {settingsTab === 'paypal' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">PayPal Settings</h2>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${paypalSettings.client_id ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-600">{paypalSettings.client_id ? 'Configured' : 'Not Configured'}</span>
            </div>
          </div>

          <form onSubmit={handleSavePaypalSettings} className="space-y-4">
            <div>
              <Label htmlFor="paypal_environment">Environment</Label>
              <select
                id="paypal_environment"
                value={paypalSettings.environment}
                onChange={(e) => setPaypalSettings({ ...paypalSettings, environment: e.target.value })}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Use sandbox for testing, switch to production for live payments</p>
            </div>

            <div>
              <Label htmlFor="paypal_client_id">Client ID</Label>
              <Input
                id="paypal_client_id"
                value={paypalSettings.client_id}
                onChange={(e) => setPaypalSettings({ ...paypalSettings, client_id: e.target.value })}
                placeholder="Enter PayPal Client ID"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Find this in your PayPal Developer Dashboard</p>
            </div>

            <div>
              <Label htmlFor="paypal_client_secret">Client Secret</Label>
              <Input
                id="paypal_client_secret"
                type="password"
                value={paypalSettings.client_secret}
                onChange={(e) => setPaypalSettings({ ...paypalSettings, client_secret: e.target.value })}
                placeholder={paypalSettings.client_secret ? '••••••••' : 'Enter PayPal Client Secret'}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Keep this secret secure - never share it publicly</p>
            </div>

            <div>
              <Label htmlFor="paypal_webhook_id">Webhook ID (Optional)</Label>
              <Input
                id="paypal_webhook_id"
                value={paypalSettings.webhook_id}
                onChange={(e) => setPaypalSettings({ ...paypalSettings, webhook_id: e.target.value })}
                placeholder="Enter PayPal Webhook ID"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Required for automatic payment confirmations</p>
            </div>

            <Button
              type="submit"
              disabled={paypalLoading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {paypalLoading ? 'Saving...' : 'Save PayPal Settings'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">📘 PayPal Setup Guide</h3>
            <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
              <li>Go to <a href="https://developer.paypal.com/" target="_blank" rel="noopener noreferrer" className="underline">developer.paypal.com</a></li>
              <li>Create or log into your PayPal Developer account</li>
              <li>Go to Dashboard → My Apps & Credentials</li>
              <li>Create a new app or select existing one</li>
              <li>Copy the Client ID and Secret</li>
              <li>For webhooks, create a webhook URL: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/api/webhook/paypal</code></li>
            </ol>
          </div>
        </div>
      )}

      {/* Email Settings Tab */}
      {settingsTab === 'email' && (
        <EmailSettingsSection API={API} />
      )}

      {/* Cancellation Policy Tab */}
      {settingsTab === 'cancellation' && (
        <div className="max-w-2xl">
          <CancellationPolicySettings />
        </div>
      )}

      {/* Branding Tab */}
      {settingsTab === 'branding' && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">🎨 Branding Settings</h2>
          
          <div className="space-y-6">
            {/* Hotel Name */}
            <div>
              <Label htmlFor="hotel_name">Hotel Name</Label>
              <Input
                id="hotel_name"
                type="text"
                value={settings.hotel_name || 'Kaiser Bridge Hotel'}
                onChange={(e) => updateSettings({ hotel_name: e.target.value })}
                placeholder="Kaiser Bridge Hotel"
              />
            </div>

            {/* Logo URL */}
            <div>
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                type="url"
                value={settings.logo_url || ''}
                onChange={(e) => updateSettings({ logo_url: e.target.value })}
                placeholder="https://yourdomain.com/logo.png"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the direct URL to your logo image (PNG, JPG, or SVG)
              </p>
            </div>

            {/* Logo Preview */}
            {settings.logo_url && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label>Logo Preview</Label>
                <div className="mt-2 flex items-center justify-center bg-white p-4 rounded border">
                  <img 
                    src={settings.logo_url} 
                    alt="Logo Preview" 
                    className="max-h-20 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="text-red-500 text-sm hidden">
                    ❌ Invalid image URL
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📝 How to upload your logo:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Upload your logo to an image hosting service (Imgur, Cloudinary, or your website)</li>
                <li>Copy the direct image URL (must end with .png, .jpg, or .svg)</li>
                <li>Paste the URL in the "Logo URL" field above</li>
                <li>Changes are saved automatically as you type</li>
              </ol>
              <p className="text-sm text-blue-800 mt-2">
                💡 Recommended size: 200x60 pixels (transparent PNG works best)
              </p>
            </div>

            {/* Footer Settings Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📍 Footer Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Address</Label>
                  <Input
                    value={settings.footer_address || ''}
                    onChange={(e) => updateSettings({ footer_address: e.target.value })}
                    placeholder="e.g., Kaiser's Bridge, Benitses, Corfu, Greece"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={settings.footer_phone || ''}
                    onChange={(e) => updateSettings({ footer_phone: e.target.value })}
                    placeholder="e.g., +30 26610 72120"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={settings.footer_email || ''}
                    onChange={(e) => updateSettings({ footer_email: e.target.value })}
                    placeholder="e.g., info@corfuhotelkaiser.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Copyright Text / Hotel Name</Label>
                  <Input
                    value={settings.footer_copyright || ''}
                    onChange={(e) => updateSettings({ footer_copyright: e.target.value })}
                    placeholder="e.g., Kaiser Bridge Hotel"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">This appears in the copyright notice: © 2025 [Your Text]. All rights reserved.</p>
                </div>
              </div>
            </div>

            {/* Google Analytics */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                📊 Google Analytics
              </h3>
              <div>
                <Label>Google Analytics Measurement ID</Label>
                <Input
                  value={settings.google_analytics_id || ''}
                  onChange={(e) => updateSettings({ google_analytics_id: e.target.value })}
                  placeholder="e.g., G-XXXXXXXXXX"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your Google Analytics 4 Measurement ID to track website visitors. 
                  Find it in Google Analytics → Admin → Data Streams → Your Stream.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Users Tab */}
      {settingsTab === 'admin-users' && (
        <AdminUsersManagement API={API} />
      )}

      {/* Database Management Tab */}
      {settingsTab === 'database' && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-red-800 mb-2">⚠️ Danger Zone</h3>
                <p className="text-sm text-red-700 mb-4">
                  These actions cannot be undone. Please be careful!
                </p>
              </div>

              {/* Clear All Bookings */}
              <div className="border border-red-200 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">
                      Clear All Bookings
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Remove all bookings from the database. This is useful for clearing test data 
                      before going live. This will also clear all payment transactions.
                    </p>
                    <p className="text-xs text-red-600 font-medium">
                      ⚠️ WARNING: This action is permanent and cannot be undone!
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('⚠️ Are you sure you want to DELETE ALL BOOKINGS? This cannot be undone!')) {
                        return;
                      }
                      if (!window.confirm('⚠️ FINAL WARNING: This will permanently delete all bookings and payment records. Type DELETE in the next prompt to confirm.')) {
                        return;
                      }
                      const confirmation = window.prompt('Type DELETE (in capital letters) to confirm:');
                      if (confirmation !== 'DELETE') {
                        toast.error('Deletion cancelled - incorrect confirmation');
                        return;
                      }

                      try {
                        const response = await fetch(`${API}/admin/clear-bookings`, {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                          }
                        });

                        if (response.ok) {
                          const data = await response.json();
                          const message = `Successfully deleted ${data.deleted_bookings || data.deleted_count} bookings` +
                            (data.deleted_transactions ? ` and ${data.deleted_transactions} payment transactions` : '');
                          toast.success(message);
                        } else {
                          toast.error('Failed to clear bookings');
                        }
                      } catch (error) {
                        console.error('Error clearing bookings:', error);
                        toast.error('Error clearing bookings');
                      }
                    }}
                    className="ml-4 bg-red-600 hover:bg-red-700"
                  >
                    Clear All Bookings
                  </Button>
                </div>
              </div>

              {/* Clear Test Data (Future) */}
              <div className="border border-gray-200 rounded-lg p-6 opacity-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">
                      Clear Test Data
                    </h4>
                    <p className="text-sm text-gray-600">
                      Remove all test bookings (bookings with test emails) while keeping real customer data.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Coming soon...
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    disabled
                    className="ml-4"
                  >
                    Clear Test Data
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Debug Logs Tab */}
      {settingsTab === 'debug' && (
        <DebugLogsSection API={API} />
      )}

      {/* Beds24 Audit Tab */}
      {settingsTab === 'beds24-audit' && (
        <Beds24AuditCheck API={API} />
      )}

      {/* Min Stay Rules Tab */}
      {settingsTab === 'min-stay' && (
        <MinStayRulesView API={API} />
      )}

      {/* Backup & Restore Tab */}
      {settingsTab === 'backup' && (
        <div className="space-y-6">
          {/* Scheduled Backups Section */}
          <ScheduledBackups API={API} />

          {/* Manual Export / Import */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Export Backup */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold">Export as JSON</h3>
                    <p className="text-sm text-gray-600 mt-1">Download complete database as JSON file</p>
                  </div>
                </div>

                <Button
                  onClick={handleExportBackup}
                  disabled={loading}
                  data-testid="export-json-btn"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export JSON
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Import Backup */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Upload className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold">Import Database</h3>
                    <p className="text-sm text-gray-600 mt-1">Restore from backup</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Input
                    type="file"
                    accept=".json"
                    onChange={(e) => setBackupFile(e.target.files[0])}
                    disabled={loading}
                    data-testid="import-file-input"
                  />
                  
                  <Button
                    onClick={handleImportBackup}
                    disabled={!backupFile || loading}
                    data-testid="import-restore-btn"
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Restoring...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Restore Backup
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

// Beds24 Audit Check Component
const Beds24AuditCheck = ({ API }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/admin/beds24/check-discrepancies`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
        body: formData,
      });
      if (res.ok) {
        setResult(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Check failed');
      }
    } catch (err) {
      toast.error('Request failed');
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary;

  return (
    <div className="space-y-4" data-testid="beds24-audit-card">
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-1">Beds24 Discrepancy Check</h3>
          <p className="text-sm text-gray-500 mb-4">Upload an Excel export from Beds24 to compare with local bookings</p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-sm mb-1 block">Beds24 Excel File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} data-testid="audit-file-input" />
            </div>
            <Button onClick={handleCheck} disabled={!file || loading} data-testid="run-audit-btn" className="bg-rose-600 hover:bg-rose-700">
              {loading ? <Loader className="w-4 h-4 mr-1 animate-spin" /> : null}
              {loading ? 'Checking...' : 'Run Check'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <>
          {/* Summary */}
          <Card>
            <CardContent className="p-6">
              <h4 className="font-semibold mb-3">Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{s.beds24_total}</p>
                  <p className="text-xs text-blue-600">In Beds24</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{s.matched}</p>
                  <p className="text-xs text-green-600">Matched</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${s.discrepancies_count > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold ${s.discrepancies_count > 0 ? 'text-amber-700' : 'text-green-700'}`}>{s.discrepancies_count}</p>
                  <p className={`text-xs ${s.discrepancies_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>Discrepancies</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${s.missing_in_local > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold ${s.missing_in_local > 0 ? 'text-red-700' : 'text-green-700'}`}>{s.missing_in_local}</p>
                  <p className={`text-xs ${s.missing_in_local > 0 ? 'text-red-600' : 'text-green-600'}`}>Missing Locally</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${s.missing_in_beds24 > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold ${s.missing_in_beds24 > 0 ? 'text-red-700' : 'text-green-700'}`}>{s.missing_in_beds24}</p>
                  <p className={`text-xs ${s.missing_in_beds24 > 0 ? 'text-red-600' : 'text-green-600'}`}>Missing in Beds24</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Missing in Local */}
          {result.missing_in_local.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold mb-3 text-red-700">Missing in Local Database ({result.missing_in_local.length})</h4>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {result.missing_in_local.map((m) => (
                    <div key={m.beds24_id} className="px-4 py-2 text-sm">
                      <span className="font-medium">#{m.beds24_id}</span> — {m.guest_name} — {m.check_in} to {m.check_out} — EUR {m.charges} — <span className="text-gray-500">{m.referrer}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Missing in Beds24 */}
          {result.missing_in_beds24.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold mb-3 text-red-700">Missing in Beds24 Export ({result.missing_in_beds24.length})</h4>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {result.missing_in_beds24.map((m) => (
                    <div key={m.local_id} className="px-4 py-2 text-sm">
                      <span className="font-medium">{m.local_id}</span> — {m.guest_name} — {m.check_in} to {m.check_out} — EUR {m.total_amount}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Discrepancies */}
          {result.discrepancies.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold mb-3 text-amber-700">Discrepancies ({result.discrepancies.length})</h4>
                <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                  {result.discrepancies.map((d) => (
                    <div key={d.beds24_id} className="px-4 py-3 text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium">{d.guest_name}</span>
                        <span className="text-xs text-gray-500">Beds24 #{d.beds24_id} / Local {d.local_id}</span>
                      </div>
                      <div className="space-y-1">
                        {d.issues.map((issue, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded font-medium ${
                              issue.field === 'status' ? 'bg-purple-100 text-purple-700' :
                              issue.field === 'charges' ? 'bg-orange-100 text-orange-700' :
                              issue.field.includes('check_') ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{issue.field}</span>
                            <span className="text-gray-600">Beds24: <strong>{String(issue.beds24)}</strong></span>
                            <span className="text-gray-400">vs</span>
                            <span className="text-gray-600">Local: <strong>{String(issue.local)}</strong></span>
                            {issue.difference && <span className="text-red-600">(diff: EUR {issue.difference})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// Min Stay Rules Read-Only View
const MinStayRulesView = ({ API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/min-stay-rules`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      toast.error('Failed to load min stay rules');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/admin/beds24/sync-min-stay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Synced ${result.total_rules} rules for ${result.room_types_synced} room types`);
        fetchRules();
      } else {
        toast.error('Sync failed');
      }
    } catch (err) {
      toast.error('Sync request failed');
    } finally {
      setSyncing(false);
    }
  };

  const colorForNights = (n) => {
    if (n <= 3) return 'bg-green-100 text-green-800';
    if (n <= 5) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <Card data-testid="min-stay-rules-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Minimum Stay Rules</h3>
            <p className="text-sm text-gray-500">Synced from Beds24 rate plans (read-only)</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchRules} disabled={loading} data-testid="refresh-min-stay-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-min-stay-btn" className="bg-teal-600 hover:bg-teal-700">
              {syncing ? <Loader className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Sync from Beds24
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : !data || data.total_room_types === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No min stay rules synced yet. Click "Sync from Beds24" to fetch them.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="min-stay-rules-list">
            {data.room_types.map((rt) => (
              <div key={rt.room_type_id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-sm">{rt.room_type_name}</h4>
                  {rt.synced_at && (
                    <span className="text-xs text-gray-400">
                      Last sync: {new Date(rt.synced_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {rt.ranges.map((r, i) => (
                    <div key={i} className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${colorForNights(r.min_stay)}`}>
                      {r.date_from === r.date_to
                        ? new Date(r.date_from + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                        : `${new Date(r.date_from + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(r.date_to + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                      }
                      : {r.min_stay}N
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Scheduled Backups Component
const ScheduledBackups = ({ API }) => {
  const [backups, setBackups] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);

  const fetchBackups = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${API}/admin/backups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBackups(data.backups || []);
      }
    } catch (err) {
      toast.error('Failed to load backups');
    } finally {
      setLoadingList(false);
    }
  }, [API]);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleTriggerBackup = async () => {
    setTriggerLoading(true);
    try {
      const res = await fetch(`${API}/admin/trigger-backup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Backup created: ${data.filename || 'success'}`);
        fetchBackups();
      } else {
        toast.error(data.detail || 'Backup failed');
      }
    } catch (err) {
      toast.error('Backup request failed — check server logs');
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const res = await fetch(`${API}/admin/backups/${filename}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Download failed');
      }
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/backups/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (res.ok) {
        toast.success('Backup deleted');
        fetchBackups();
      } else {
        toast.error('Failed to delete backup');
      }
    } catch (err) {
      toast.error('Delete request failed');
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card data-testid="scheduled-backups-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Database Backups</h3>
              <p className="text-sm text-gray-500">Automatic daily backups (kept for 7 days)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBackups}
              disabled={loadingList}
              data-testid="refresh-backups-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={handleTriggerBackup}
              disabled={triggerLoading}
              data-testid="trigger-backup-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {triggerLoading ? (
                <Loader className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <HardDrive className="w-4 h-4 mr-1" />
              )}
              Backup Now
            </Button>
          </div>
        </div>

        {loadingList ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <Loader className="w-5 h-5 animate-spin mr-2" />
            Loading backups...
          </div>
        ) : backups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <HardDrive className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No backups yet. Click "Backup Now" to create one.</p>
          </div>
        ) : (
          <div className="border rounded-lg divide-y" data-testid="backups-list">
            {backups.map((b) => (
              <div key={b.filename} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50" data-testid={`backup-item-${b.filename}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{b.filename}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(b.created_at).toLocaleString()} &middot; {formatSize(b.size)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(b.filename)}
                    data-testid={`download-backup-${b.filename}`}
                    title="Download"
                  >
                    <Download className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(b.filename)}
                    data-testid={`delete-backup-${b.filename}`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Debug Logs Section Component
const DebugLogsSection = ({ API }) => {
  const [logs, setLogs] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [logType, setLogType] = React.useState('backend');
  const [lines, setLines] = React.useState(100);
  const [filter, setFilter] = React.useState('');
  const [bookingId, setBookingId] = React.useState('');
  const [bookingDebug, setBookingDebug] = React.useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        log_type: logType,
        lines: lines.toString(),
      });
      if (filter) params.append('filter', filter);

      const response = await fetch(`${API}/admin/debug/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else {
        toast.error('Failed to fetch logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error fetching logs');
    }
    setLoading(false);
  };

  const fetchBookingDebug = async () => {
    if (!bookingId.trim()) {
      toast.error('Please enter a booking ID');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/debug/booking/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBookingDebug(data);
      } else {
        toast.error('Booking not found');
      }
    } catch (error) {
      console.error('Error fetching booking debug:', error);
      toast.error('Error fetching booking debug info');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Log Viewer */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">🐛 Application Logs</h3>
          
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[150px]">
              <Label>Log Type</Label>
              <select
                value={logType}
                onChange={(e) => setLogType(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="backend">Backend Errors</option>
                <option value="backend_out">Backend Output</option>
                <option value="frontend">Frontend</option>
                <option value="all">All Logs</option>
              </select>
            </div>
            
            <div className="w-24">
              <Label>Lines</Label>
              <Input
                type="number"
                value={lines}
                onChange={(e) => setLines(parseInt(e.target.value) || 100)}
                min={10}
                max={500}
                className="mt-1"
              />
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label>Filter (keyword)</Label>
              <Input
                placeholder="e.g., nexi, beds24, email"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-end">
              <Button onClick={fetchLogs} disabled={loading}>
                {loading ? 'Loading...' : '🔍 Fetch Logs'}
              </Button>
            </div>
          </div>

          {logs && (
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-2">
                Fetched at: {new Date(logs.timestamp).toLocaleString()}
                {logs.filter && ` | Filter: "${logs.filter}"`}
              </p>
              
              {Object.entries(logs.logs).map(([key, logData]) => (
                <div key={key} className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">
                    {key} ({logData.line_count} lines)
                  </h4>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs max-h-96 overflow-y-auto">
                    {logData.content || logData.error || 'No logs found'}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Debug */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">🔍 Debug Specific Booking</h3>
          
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Label>Booking ID</Label>
              <Input
                placeholder="e.g., BK123456"
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchBookingDebug} disabled={loading}>
                {loading ? 'Loading...' : '🔍 Debug Booking'}
              </Button>
            </div>
          </div>

          {bookingDebug && (
            <div className="mt-4 space-y-4">
              {/* Quick Status */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-100 p-3 rounded">
                  <p className="text-xs text-gray-500">Payment Status</p>
                  <p className={`font-semibold ${
                    bookingDebug.booking?.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {bookingDebug.booking?.payment_status || 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-100 p-3 rounded">
                  <p className="text-xs text-gray-500">Has Nexi Order ID</p>
                  <p className="font-semibold">{bookingDebug.debug_info?.has_nexi_order_id ? '✅ Yes' : '❌ No'}</p>
                </div>
                <div className="bg-gray-100 p-3 rounded">
                  <p className="text-xs text-gray-500">Beds24 Synced</p>
                  <p className="font-semibold">
                    {bookingDebug.debug_info?.beds24_booking_ids?.length > 0 ? '✅ Yes' : '❌ No'}
                  </p>
                </div>
                <div className="bg-gray-100 p-3 rounded">
                  <p className="text-xs text-gray-500">Payment Option</p>
                  <p className="font-semibold">{bookingDebug.debug_info?.payment_option || 'N/A'}</p>
                </div>
              </div>

              {/* Nexi Status */}
              {bookingDebug.nexi_order_status && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Nexi Order Status (Live)</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto max-h-48 overflow-y-auto">
                    {JSON.stringify(bookingDebug.nexi_order_status, null, 2)}
                  </pre>
                </div>
              )}

              {/* Booking Data */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Booking Data</h4>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto max-h-48 overflow-y-auto">
                  {JSON.stringify(bookingDebug.booking, null, 2)}
                </pre>
              </div>

              {/* Transactions */}
              {bookingDebug.payment_transactions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Payment Transactions</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto max-h-48 overflow-y-auto">
                    {JSON.stringify(bookingDebug.payment_transactions, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};